import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateStaff } from '@/hooks/useStaff';
import { useKitchenSpecialties } from '@/hooks/useKitchenSpecialties';
import { useZones } from '@/hooks/useZones';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Plus, RefreshCw } from 'lucide-react';
import {
  validatePassword,
  validateUsername,
  generateSecurePassword,
} from '@/lib/password-validation';

interface CreateStaffDialogProps {
  restaurantId: string;
  onSuccess?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function CreateStaffDialog({
  restaurantId,
  onSuccess,
  open: controlledOpen,
  onOpenChange,
}: CreateStaffDialogProps) {
  const { t, i18n } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);

  // Use controlled or internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: '' as 'server' | 'kitchen' | 'printer' | '',
    specialtyId: '' as string,
    zoneIds: [] as string[],
    receiptHeader: '',
    receiptFooter: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [passwordStrength, setPasswordStrength] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  const createStaffMutation = useCreateStaff();
  const { data: specialties } = useKitchenSpecialties(restaurantId);
  const { data: zones } = useZones(restaurantId);

  const handleGeneratePassword = () => {
    const newPassword = generateSecurePassword(12);
    setFormData({ ...formData, password: newPassword });
    handlePasswordChange(newPassword);
  };

  const handlePasswordChange = (password: string) => {
    const validation = validatePassword(password);
    setPasswordStrength(validation.errors);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Prevent double submission with ref (immediate check)
    if (isSubmittingRef.current) {
      console.log('Duplicate submission blocked by ref');
      return;
    }
    if (isSubmitting) {
      console.log('Duplicate submission blocked by state');
      return;
    }

    console.log('Starting staff creation submission');
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setErrors({});

    // Validate
    const newErrors: Record<string, string> = {};

    if (!formData.username) {
      newErrors['username'] = t('validation.required');
    } else {
      const usernameValidation = validateUsername(formData.username);
      if (!usernameValidation.isValid && usernameValidation.errors[0]) {
        newErrors['username'] = usernameValidation.errors[0];
      }
    }

    if (!formData.password) {
      newErrors['password'] = t('validation.required');
    } else {
      const passwordValidation = validatePassword(formData.password);
      if (!passwordValidation.isValid && passwordValidation.errors[0]) {
        newErrors['password'] = passwordValidation.errors[0];
      }
    }

    if (!formData.role) {
      newErrors['role'] = t('validation.required');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      return;
    }

    try {
      const mutationData: {
        restaurantId: string;
        username: string;
        password: string;
        role: 'server' | 'kitchen' | 'printer';
        specialtyId?: string | null;
        zoneId?: string | null;
        zoneIds?: string[];
        receiptHeader?: string | null;
        receiptFooter?: string | null;
      } = {
        restaurantId,
        username: formData.username,
        password: formData.password,
        role: formData.role as 'server' | 'kitchen' | 'printer',
        specialtyId:
          formData.role === 'kitchen' && formData.specialtyId ? formData.specialtyId : null,
      };

      if (formData.role === 'server' && formData.zoneIds.length > 0) {
        mutationData.zoneIds = formData.zoneIds;
      }

      if (formData.role === 'printer') {
        mutationData.receiptHeader = formData.receiptHeader || null;
        mutationData.receiptFooter = formData.receiptFooter || null;
      }

      await createStaffMutation.mutateAsync(mutationData);
      setOpen(false);
      setFormData({ username: '', password: '', role: '', specialtyId: '', zoneIds: [], receiptHeader: '', receiptFooter: '' });
      setPasswordStrength([]);
      onSuccess?.();
    } catch (error: any) {
      // Check for duplicate key error (username already used, possibly deleted)
      const errorMessage = error?.message || '';
      const errorCode = error?.code || '';
      const isDuplicateKeyError =
        errorCode === '23505' ||
        errorMessage.includes('duplicate key') ||
        errorMessage.includes('already exists');

      if (isDuplicateKeyError) {
        setErrors({
          form: t('staff.usernameAlreadyUsed'),
        });
      } else {
        setErrors({
          form: error instanceof Error ? error.message : t('error.generic'),
        });
      }
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {controlledOpen === undefined && (
        <DialogTrigger asChild>
          <Button>
            <Plus className="w-4 h-4 me-2" />
            <span className="truncate">{t('staff.createStaff')}</span>
          </Button>
        </DialogTrigger>
      )}
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{t('staff.createStaff')}</DialogTitle>
            <DialogDescription>{t('staff.createStaffDescription')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="username">{t('staff.username')}</Label>
              <Input
                id="username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                disabled={isSubmitting || createStaffMutation.isPending}
                placeholder="server1"
              />
              <p className="text-xs text-muted-foreground">
                {t('staff.emailAutoGenerated') ||
                  'Email will be auto-generated as: {username}@restaurant-id.com'}
              </p>
              {errors['username'] && (
                <p className="text-sm text-destructive">{errors['username']}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('staff.password')}</Label>
              <div className="flex gap-2">
                <Input
                  id="password"
                  type="text"
                  value={formData.password}
                  onChange={(e) => {
                    setFormData({ ...formData, password: e.target.value });
                    handlePasswordChange(e.target.value);
                  }}
                  disabled={isSubmitting || createStaffMutation.isPending}
                  placeholder="••••••"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleGeneratePassword}
                  disabled={isSubmitting || createStaffMutation.isPending}
                  title="Generate secure password"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
              {passwordStrength.length > 0 && (
                <div className="text-xs text-muted-foreground space-y-1">
                  {passwordStrength.map((error, index) => (
                    <p key={index} className="text-amber-600">
                      • {error}
                    </p>
                  ))}
                </div>
              )}
              {errors['password'] && (
                <p className="text-sm text-destructive">{errors['password']}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">{t('staff.role')}</Label>
              <Select
                value={formData.role}
                onValueChange={(value: 'server' | 'kitchen' | 'printer') =>
                  setFormData({ ...formData, role: value })
                }
                disabled={isSubmitting || createStaffMutation.isPending}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('staff.selectRole')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="server">{t('user.server')}</SelectItem>
                  <SelectItem value="kitchen">{t('user.kitchen')}</SelectItem>
                  <SelectItem value="printer">{t('user.printer')}</SelectItem>
                </SelectContent>
              </Select>
              {errors['role'] && <p className="text-sm text-destructive">{errors['role']}</p>}
            </div>

            {/* Specialty selector - only for kitchen role */}
            {formData.role === 'kitchen' && specialties && specialties.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="specialty">{t('settings.kitchenSpecialties')}</Label>
                <Select
                  value={formData.specialtyId || 'none'}
                  onValueChange={(value) =>
                    setFormData({ ...formData, specialtyId: value === 'none' ? '' : value })
                  }
                  disabled={isSubmitting || createStaffMutation.isPending}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('settings.noSpecialtyAssigned')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('settings.noSpecialtyAssigned')}</SelectItem>
                    {specialties
                      .filter((s) => s.isActive)
                      .map((specialty) => (
                        <SelectItem key={specialty.id} value={specialty.id}>
                          {i18n.language === 'ar' ? specialty.nameAr : specialty.nameFr}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Zone selector - only for server role */}
            {formData.role === 'server' && zones && zones.length > 0 && (
              <div className="space-y-2">
                <Label>{t('staff.zones') || 'Zones'}</Label>
                <div className="space-y-2 border rounded-md p-3 max-h-48 overflow-y-auto">
                  {zones
                    .filter((z) => z.isActive)
                    .map((zone) => (
                      <div key={zone.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`zone-${zone.id}`}
                          checked={formData.zoneIds.includes(zone.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFormData({
                                ...formData,
                                zoneIds: [...formData.zoneIds, zone.id],
                              });
                            } else {
                              setFormData({
                                ...formData,
                                zoneIds: formData.zoneIds.filter((id) => id !== zone.id),
                              });
                            }
                          }}
                          disabled={isSubmitting || createStaffMutation.isPending}
                        />
                        <Label
                          htmlFor={`zone-${zone.id}`}
                          className="text-sm font-normal cursor-pointer flex-1"
                        >
                          {i18n.language === 'ar' ? zone.nameAr : zone.nameFr}
                        </Label>
                      </div>
                    ))}
                  {zones.filter((z) => z.isActive).length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      {t('staff.noZonesAvailable') || 'No zones available'}
                    </p>
                  )}
                </div>
                {formData.zoneIds.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    {t('staff.selectZonesHint') || 'Select one or more zones for this server'}
                  </p>
                )}
              </div>
            )}

            {/* Receipt header and footer - only for printer role */}
            {formData.role === 'printer' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="receiptHeader">{t('staff.receiptHeader')}</Label>
                  <Textarea
                    id="receiptHeader"
                    value={formData.receiptHeader}
                    onChange={(e) => setFormData({ ...formData, receiptHeader: e.target.value })}
                    disabled={isSubmitting || createStaffMutation.isPending}
                    placeholder={t('staff.receiptHeaderPlaceholder')}
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="receiptFooter">{t('staff.receiptFooter')}</Label>
                  <Textarea
                    id="receiptFooter"
                    value={formData.receiptFooter}
                    onChange={(e) => setFormData({ ...formData, receiptFooter: e.target.value })}
                    disabled={isSubmitting || createStaffMutation.isPending}
                    placeholder={t('staff.receiptFooterPlaceholder')}
                    rows={3}
                  />
                </div>
              </>
            )}

            {errors['form'] && (
              <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                {errors['form']}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isSubmitting || createStaffMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting || createStaffMutation.isPending}>
              {isSubmitting || createStaffMutation.isPending
                ? t('common.loading')
                : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
